import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import AuthService, { User } from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    fullWidth: {
        width: '100%',
    },
    passBold: {
        color: theme.palette.secondary.main,
        marginLeft: '5px',
        marginRight: '5px',
    },
    listItem: {
        display: 'block',
    }
}));

const Password = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const routeParams = useParams();
    const { state } = useLocation();
    const [expired, setExpired] = useState(false);
    const [password, setPassword] = useState({
        password: '',
        confirm: '',
        token: '',
        current_password: '',
        username: '',
    });
    const user = User();
    const passwordForm = useValidate({});
    const [original] = useState(_cloneDeep(password));
    const [changes, setChanges] = useState(false);

    useEffect(() => {
        setChanges(!_isEqual(password, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [password]);

    useEffect(() => {
        setExpired(user.password_expired);
    }, [user]);

    const handleUpdatePasswordData = (e, validID, valid) => {
        passwordForm.set({ ...passwordForm.get(), [validID]: valid });

        setPassword({
            ...password,
            [e.target.name]: e.target.value,
        });
    };

    const handleSetPassword = () => {
        // continue down the road if there's another state.next
        const replace = _get(state, 'next.length', 0) > 0;
        const next = _get(state, 'next.length', 0) > 0 ? state.next.pop() : -1;

        AuthService.setPassword(routeParams.username, password).then(() => {
            toast.success('Password set successfully');
            navigate(next, { replace, state });
        }, err => {
            if (err.errors) {
                if (err.errors.password) {
                    toast.error(err.errors.password);
                } else if (err.errors.current_password) {
                    toast.error(err.errors.current_password);
                }
            }
        });
    };

    const handleCancel = () => {
        if (expired) {
            // no unnecessary logouts
            AuthService.boot();
        } else {
            navigate(-1);
        }
    };

    useEffect(() => {
        if (routeParams.username) {
            AuthService.getPasswordToken(routeParams.username).then(token => {
                setPassword({
                    ...password,
                    token: (expired || user.username === routeParams.username) ? '' : token,
                    username: routeParams.username,
                });
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container>
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box mt={2} p={3}>
                        <Grid container>
                            <Grid container item xs={8} spacing={2} alignContent="flex-start">
                                <Grid item xs={12}>
                                    <Typography variant="h5">Set Password - {routeParams.username}</Typography>
                                </Grid>
                                {(expired || user.username === routeParams.username) && <Grid item xs={12}>
                                    <Validator
                                        label="Current Password"
                                        type="password"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        value={password.current_password}
                                        name="current_password"
                                        onChange={handleUpdatePasswordData}
                                    />
                                </Grid>}
                                <Grid item xs={12}>
                                    <Validator
                                        valueType="password"
                                        errorText="Password is invalid"
                                        label="New Password"
                                        type="password"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        value={password.password}
                                        name="password"
                                        onChange={handleUpdatePasswordData}
                                    />
                                    <Validator
                                        valueType="password"
                                        equal={password.password}
                                        errorText="Password does not match"
                                        label="Confirm Password"
                                        type="password"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        value={password.confirm}
                                        name="confirm"
                                        onChange={handleUpdatePasswordData}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        className={classes.buttonRight}
                                        aria-label="set password"
                                        onClick={handleSetPassword}
                                        disabled={!(checkValidity(passwordForm.get()) && changes)}
                                    >Set Password</Button>
                                    <Button variant="contained" className={classes.buttonRight} aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                                </Grid>
                            </Grid>
                            <Grid item xs={4}>
                                <Box mt={2} p={3}>
                                    <Typography>Passwords must:</Typography>
                                    <List>
                                        <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more uppercase </b> characters (A-Z)</ListItem>
                                        <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more lowercase </b> characters (a-z)</ListItem>
                                        <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more digits </b> (0-9)</ListItem>
                                        <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more non-alphanumeric </b> characters: - + _ ! @ # $ % ^ &amp; * . , ?</ListItem>
                                        <ListItem className={classes.listItem}>have <b className={classes.passBold}>8 characters</b> or more</ListItem>
                                        <ListItem className={classes.listItem}>have <b className={classes.passBold}>not been used</b> as one of previous passwords</ListItem>
                                    </List>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Password;
